/**
 * Background post composer service and validation rules.
 *
 * Implements strict constraints matching lab-server/schemas/dispatch.schema.js
 * and server-authoritative preset catalog from GET /api/fbi/background-presets.
 *
 * Constraints:
 * - Server authoritative availability: presets are fetched from GET /api/fbi/background-presets.
 * - Only enabled entries can be dispatched; disabled/discovered entries are blocked.
 * - Colors must be validated hex (#RRGGBB); no raw CSS, scripts, or external URLs.
 * - Null preview displays neutral marker; no fake or guessed colors.
 * - Unsupported contexts: only personal accounts in groups; no Pages, profiles, anonymous, media, or crosspost.
 * - Content boundaries: subject to product global maximum 63,206 characters; background-specific 130 chars/4 lines limit removed per user request.
 */

import type { CSSProperties } from 'react';
import type {
  BackgroundPresetItem,
  BackgroundPresetPreview,
  BackgroundPresetPreviewType,
  BackgroundPresetScope,
  BackgroundPresetStatus,
  BackgroundPresetsResponse,
} from './kieu';

export type {
  BackgroundPresetItem,
  BackgroundPresetPreview,
  BackgroundPresetPreviewType,
  BackgroundPresetScope,
  BackgroundPresetStatus,
  BackgroundPresetsResponse,
};

export const VERIFIED_BACKGROUND_PRESET_RED = '1903718606535395';
export const VERIFIED_PRESET_NAME = 'Đỏ';
export const VERIFIED_PRESET_LABEL = 'Phông nền đỏ (preset 1903718606535395)';
export const NONE_PRESET_NAME = 'Không nền';
export const NONE_PRESET_LABEL = 'Không dùng phông nền';

export interface BackgroundPresetOption {
  id: string | null;
  name: string;
  colorHex?: string;
  bgClass: string;
  title: string;
}

export const SUPPORTED_BACKGROUND_PRESETS: readonly BackgroundPresetOption[] = [
  {
    id: null,
    name: NONE_PRESET_NAME,
    bgClass: 'bg-transparent border border-slate-300 dark:border-slate-600',
    title: NONE_PRESET_LABEL,
  },
  {
    id: VERIFIED_BACKGROUND_PRESET_RED,
    name: VERIFIED_PRESET_NAME,
    colorHex: '#dc2626',
    bgClass: 'bg-red-600 border border-red-700 text-white',
    title: VERIFIED_PRESET_LABEL,
  },
] as const;

export interface BackgroundContext {
  isPage?: boolean;
  postToProfile?: boolean;
  isAnonymous?: boolean;
  anDanh?: boolean;
  isCrossPost?: boolean;
  hasMedia?: boolean;
  charCount?: number;
  lineCount?: number;
  text?: string;
  message?: string;
}

export interface BackgroundSubmitValidationContext extends BackgroundContext {
  backgroundId: string | null | undefined;
  groupIdCount?: number;
  presets?: readonly BackgroundPresetItem[] | null;
}

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const PRESET_ID_REGEX = /^\d{5,30}$/;

export function isValidHexColor(color: unknown): color is string {
  return typeof color === 'string' && HEX_COLOR_REGEX.test(color);
}

export function isValidPresetId(id: unknown): id is string {
  return typeof id === 'string' && PRESET_ID_REGEX.test(id);
}

/**
 * Validates and sanitizes preview payload from server.
 * Rejects raw CSS, scripts, external URLs, and partial malformed color arrays.
 * Gracefully supports future image preview type while restricting to safe URLs.
 */
export function validateAndSanitizePreview(rawPreview: unknown): BackgroundPresetPreview | null {
  if (!rawPreview || typeof rawPreview !== 'object') return null;
  const p = rawPreview as Record<string, unknown>;

  if (p.type !== 'solid' && p.type !== 'gradient' && p.type !== 'image') return null;

  if (p.type === 'image') {
    // Frontend image contract: exact { type: 'image', assetUrl: '/api/fbi/background-presets/<numericId>/preview', fallbackColor?: '#RRGGBB' }
    // Strictly rejects arbitrary URLs, external URLs, and legacy imageUrl/url keys.
    const assetUrl = p.assetUrl;
    if (typeof assetUrl !== 'string' || !/^\/api\/fbi\/background-presets\/\d{5,30}\/preview(\?v=[a-fA-F0-9]+)?$/.test(assetUrl)) {
      return null;
    }

    let fallbackColor: string | undefined = undefined;
    if (p.fallbackColor !== undefined) {
      if (typeof p.fallbackColor !== 'string' || !isValidHexColor(p.fallbackColor)) return null;
      fallbackColor = p.fallbackColor;
    }

    const allowedKeys = new Set(['type', 'assetUrl', 'fallbackColor']);
    for (const key of Object.keys(p)) {
      if (!allowedKeys.has(key)) return null;
    }
    return {
      type: 'image',
      assetUrl,
      ...(fallbackColor ? { fallbackColor } : {}),
    };
  }

  if (!Array.isArray(p.colors) || p.colors.length === 0 || p.colors.length > 4) return null;

  for (const c of p.colors) {
    if (!isValidHexColor(c)) return null;
  }

  let angle: number | undefined = undefined;
  if (p.angle !== undefined) {
    if (typeof p.angle !== 'number' || !Number.isFinite(p.angle) || p.angle < 0 || p.angle > 360) {
      return null;
    }
    angle = p.angle;
  }

  const allowedKeys = new Set(['type', 'colors', 'angle']);
  for (const key of Object.keys(p)) {
    if (!allowedKeys.has(key)) return null;
  }

  return {
    type: p.type,
    colors: [...p.colors],
    ...(angle !== undefined ? { angle } : {}),
  };
}

/**
 * Validates and sanitizes a single preset item from server projection.
 */
export function validateAndSanitizePreset(raw: unknown): BackgroundPresetItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;

  if (!isValidPresetId(p.id)) return null;
  if (typeof p.label !== 'string' || p.label.trim().length === 0 || p.label.length > 80) return null;
  if (p.scope !== 'GROUP_PERSONAL') return null;
  if (p.status !== 'DISCOVERED' && p.status !== 'ENABLED' && p.status !== 'VERIFIED') return null;
  if (typeof p.enabled !== 'boolean') return null;

  let preview: BackgroundPresetPreview | null = null;
  if (p.preview !== null && p.preview !== undefined) {
    preview = validateAndSanitizePreview(p.preview);
    // If preview was present but malformed, preview is sanitized to null (neutral marker)
  }

  return {
    id: p.id,
    label: p.label.trim(),
    preview,
    status: p.status,
    enabled: p.enabled,
    scope: p.scope,
  };
}

/**
 * Validates an array of presets from server projection.
 */
export function validateAndSanitizePresetList(rawList: unknown): BackgroundPresetItem[] {
  if (!Array.isArray(rawList)) return [];
  const list: BackgroundPresetItem[] = [];
  for (const item of rawList) {
    const valid = validateAndSanitizePreset(item);
    if (valid) {
      list.push(valid);
    }
  }
  return list;
}

/**
 * Safely derives React style for a preview swatch using only validated hex colors and numeric angle,
 * or safe image URLs for decorative presets.
 * Returns undefined for null preview (caller renders neutral marker without fake colors).
 */
export function getPresetSwatchStyle(preview: BackgroundPresetPreview | null): CSSProperties | undefined {
  if (!preview) return undefined;
  if (preview.type === 'solid' && preview.colors && preview.colors.length > 0) {
    return { backgroundColor: preview.colors[0] };
  }
  if (preview.type === 'gradient' && preview.colors && preview.colors.length > 0) {
    const angle = preview.angle ?? 135;
    return { backgroundImage: `linear-gradient(${angle}deg, ${preview.colors.join(', ')})` };
  }
  if (preview.type === 'image') {
    if (preview.assetUrl) {
      return {
        backgroundImage: `url(${preview.assetUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        ...(preview.fallbackColor ? { backgroundColor: preview.fallbackColor } : {}),
      };
    } else if (preview.fallbackColor) {
      return {
        backgroundColor: preview.fallbackColor,
      };
    }
  }
  return undefined;
}

const GLOBAL_RECENT_PRESETS_STORAGE_KEY = 'fbmax_recent_background_presets';
const MAX_RECENT_PRESETS = 8;

function getScopedRecentStorageKey(tenantId?: string | null, actorId?: string | null): string {
  const safeTenant = tenantId && typeof tenantId === 'string' && tenantId.trim().length > 0
    ? tenantId.trim()
    : 'default';
  const safeActor = actorId && typeof actorId === 'string' && actorId.trim().length > 0
    ? actorId.trim()
    : 'default';
  return `fbmax_recent_background_presets:${safeTenant}:${safeActor}`;
}

/**
 * Safely reads recently used background preset IDs from browser localStorage,
 * strictly scoped to current tenant and actor (SEC-01 / IDENTITY-01).
 */
export function getRecentBackgroundPresetIds(tenantId?: string | null, actorId?: string | null): string[] {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const scopedKey = getScopedRecentStorageKey(tenantId, actorId);
    let raw = window.localStorage.getItem(scopedKey);
    // Backward compatibility fallback to global key if scoped key is empty and no specific tenant/actor provided
    if (!raw && (!tenantId && !actorId)) {
      raw = window.localStorage.getItem(GLOBAL_RECENT_PRESETS_STORAGE_KEY);
    }
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string' && isValidPresetId(id));
  } catch {
    return [];
  }
}

/**
 * Records a selected preset ID into real usage storage, scoped by tenant and actor.
 */
export function recordRecentBackgroundPresetId(
  id: string,
  tenantId?: string | null,
  actorId?: string | null,
): void {
  if (!isValidPresetId(id)) return;
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const scopedKey = getScopedRecentStorageKey(tenantId, actorId);
    const current = getRecentBackgroundPresetIds(tenantId, actorId);
    const updated = [id, ...current.filter((item) => item !== id)].slice(0, MAX_RECENT_PRESETS);
    window.localStorage.setItem(scopedKey, JSON.stringify(updated));
  } catch {
    // Ignore storage write issues
  }
}

export interface GroupedBackgroundPresets {
  recent: BackgroundPresetItem[];
  decorative: BackgroundPresetItem[];
  gradient: BackgroundPresetItem[];
  solid: BackgroundPresetItem[];
  unpreviewed: BackgroundPresetItem[];
  disabled: BackgroundPresetItem[];
}

/**
 * Groups presets strictly by actual preview metadata and real usage.
 * - solid -> Một màu trơn
 * - gradient -> Màu chuyển sắc
 * - image -> Trang trí (server-authorized assetUrl)
 * - null/empty preview -> Chưa có hình xem trước clearly
 * - recent -> Dùng gần đây (ONLY real usage from recentIds, NEVER fabricated)
 */
export function groupBackgroundPresets(
  presets: readonly BackgroundPresetItem[] | null | undefined,
  recentIds: readonly string[] = [],
): GroupedBackgroundPresets {
  const result: GroupedBackgroundPresets = {
    recent: [],
    decorative: [],
    gradient: [],
    solid: [],
    unpreviewed: [],
    disabled: [],
  };

  if (!Array.isArray(presets) || presets.length === 0) {
    return result;
  }

  const enabledPresets = presets.filter((p) => p.enabled);
  result.disabled = presets.filter((p) => !p.enabled);

  for (const preset of enabledPresets) {
    if (!preset.preview) {
      result.unpreviewed.push(preset);
    } else if (preset.preview.type === 'solid') {
      result.solid.push(preset);
    } else if (preset.preview.type === 'gradient') {
      result.gradient.push(preset);
    } else if (preset.preview.type === 'image') {
      result.decorative.push(preset);
    } else {
      result.unpreviewed.push(preset);
    }
  }

  // Dùng gần đây: ONLY real usage matched against enabled presets. Never fabricate.
  if (Array.isArray(recentIds) && recentIds.length > 0) {
    const enabledMap = new Map(enabledPresets.map((p) => [p.id, p]));
    for (const id of recentIds) {
      const found = enabledMap.get(id);
      if (found && !result.recent.some((p) => p.id === found.id)) {
        result.recent.push(found);
      }
    }
  }

  return result;
}

/**
 * Calculates maximum possible length and lines across all spintax alternatives.
 * Handles nested `{opt1|opt2}` variations safely without regex backtracking.
 */
export function calculateMaxSpintaxLengthAndLines(text: string): { maxChars: number; maxLines: number } {
  if (!text || typeof text !== 'string') return { maxChars: 0, maxLines: 1 };
  if (!text.includes('{')) {
    return { maxChars: text.length, maxLines: text.split('\n').length };
  }

  function parseBranch(idx: number, stopAtBrace: boolean): { length: number; lines: number; nextIdx: number } {
    let currentLen = 0;
    let currentLines = 0;
    const branchLens: number[] = [];
    const branchLines: number[] = [];
    let i = idx;

    while (i < text.length) {
      const ch = text[i];
      if (ch === '\\' && i + 1 < text.length) {
        currentLen += 1;
        i += 2;
        continue;
      }
      if (ch === '\n') {
        currentLines += 1;
        currentLen += 1;
        i += 1;
        continue;
      }
      if (ch === '{') {
        const sub = parseBranch(i + 1, true);
        currentLen += sub.length;
        currentLines += sub.lines;
        i = sub.nextIdx;
        continue;
      }
      if (stopAtBrace && ch === '|') {
        branchLens.push(currentLen);
        branchLines.push(currentLines);
        currentLen = 0;
        currentLines = 0;
        i += 1;
        continue;
      }
      if (stopAtBrace && ch === '}') {
        branchLens.push(currentLen);
        branchLines.push(currentLines);
        return {
          length: Math.max(...branchLens, 0),
          lines: Math.max(...branchLines, 0),
          nextIdx: i + 1,
        };
      }
      currentLen += 1;
      i += 1;
    }

    branchLens.push(currentLen);
    branchLines.push(currentLines);
    return {
      length: Math.max(...branchLens, 0),
      lines: Math.max(...branchLines, 0),
      nextIdx: i,
    };
  }

  const result = parseBranch(0, false);
  return {
    maxChars: result.length,
    maxLines: result.lines + 1,
  };
}

/**
 * Returns a Vietnamese explanation if composer state prevents using background posts,
 * or null if background posts are eligible under the current context.
 */
export function getBackgroundIncompatibilityReason(context: BackgroundContext): string | null {
  if (context.isPage) {
    return 'Phông nền hiện chỉ hỗ trợ tài khoản cá nhân, không hỗ trợ Fanpage.';
  }
  if (context.postToProfile) {
    return 'Phông nền chỉ hỗ trợ khi đăng vào nhóm, không hỗ trợ đăng lên Trang cá nhân.';
  }
  if (context.isAnonymous || context.anDanh) {
    return 'Không thể dùng phông nền cùng Đăng ẩn danh.';
  }
  if (context.isCrossPost) {
    return 'Không thể dùng phông nền cùng Đăng chéo.';
  }
  if (context.hasMedia) {
    return 'Không thể dùng phông nền cùng ảnh hoặc video.';
  }

  const content = context.text ?? context.message;
  let charCount = context.charCount;

  if (typeof content === 'string') {
    const spintaxStats = calculateMaxSpintaxLengthAndLines(content);
    charCount = Math.max(charCount ?? 0, content.length, spintaxStats.maxChars);
  }

  if (typeof charCount === 'number' && charCount > 63206) {
    return 'Nội dung bài viết quá dài (tối đa 63.206 ký tự).';
  }
  return null;
}

export function isBackgroundSupported(context: BackgroundContext): boolean {
  return getBackgroundIncompatibilityReason(context) === null;
}

/**
 * Validates background selection before dispatch.
 * Accepts loaded server preset catalog; rejects hardcoded ID restrictions.
 * Does not silently drop user intent: returns a clear Vietnamese reason if invalid.
 */
export function validateBackgroundOnSubmit(context: BackgroundSubmitValidationContext): string | null {
  const { backgroundId, groupIdCount, presets } = context;
  if (!backgroundId) {
    return null;
  }
  if (!isValidPresetId(backgroundId)) {
    return 'Mã phông nền không hợp lệ.';
  }
  if (groupIdCount !== undefined && groupIdCount <= 0) {
    return 'Phông nền chỉ hỗ trợ khi đăng vào nhóm. Vui lòng chọn ít nhất một nhóm.';
  }

  const incompReason = getBackgroundIncompatibilityReason(context);
  if (incompReason) {
    return incompReason;
  }

  // Server authoritative verification without hardcoded bypass:
  if (Array.isArray(presets)) {
    const matched = presets.find((p) => p.id === backgroundId);
    if (!matched) {
      return 'Mã phông nền không hợp lệ hoặc không còn khả dụng trên máy chủ.';
    }
    if (!matched.enabled) {
      return 'Mã phông nền chưa được kích hoạt.';
    }
    return null;
  }

  // When no server catalog is provided, reject unknown ID to prevent bypassing server outage:
  return 'Mã phông nền không hợp lệ.';
}

/**
 * Sanitizes backgroundId loaded from browser draft storage.
 * Does not erase draft preset while catalog load is pending (presets is undefined/null).
 * Once catalog is provided, validates preset is present and enabled.
 */
export function sanitizeDraftBackground(
  rawBackgroundId: unknown,
  context: BackgroundContext,
  presets?: readonly BackgroundPresetItem[] | null,
): string | null {
  if (!isValidPresetId(rawBackgroundId)) {
    return null;
  }
  if (
    context.isPage ||
    context.postToProfile ||
    context.isAnonymous ||
    context.anDanh ||
    context.isCrossPost ||
    context.hasMedia
  ) {
    return null;
  }

  // If server preset catalog is loaded, validate against it
  if (Array.isArray(presets)) {
    const matched = presets.find((p) => p.id === rawBackgroundId && p.enabled);
    return matched ? rawBackgroundId : null;
  }

  // Preserve draft preset while load is pending
  return rawBackgroundId;
}

/**
 * Adds backgroundId string to payload only when valid.
 * Returns an error if background is chosen but context is incompatible.
 */
export function applyBackgroundToPayload(
  payload: Record<string, unknown>,
  backgroundId: string | null | undefined,
  context: BackgroundSubmitValidationContext,
): { ok: true } | { ok: false; error: string } {
  if (!backgroundId) {
    return { ok: true };
  }
  const error = validateBackgroundOnSubmit({ ...context, backgroundId });
  if (error) {
    return { ok: false, error };
  }
  payload.backgroundId = backgroundId;
  return { ok: true };
}
